import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Upload, Play, Trash2, Clock, Film,
    AlertCircle, CheckCircle, RefreshCw, Eye, EyeOff,
    Loader2, Lock, Globe, Ghost, Users, Crown
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// --- HELPER COMPONENTS ---

const handleDownloadWaitlist = async () => {
    try {
        // 1. GENERATE TIMESTAMP
        const timestamp = new Date().getTime();

        // 2. APPEND TO URL
        // We add "&t=" + timestamp to force the browser to treat this as a NEW request
        const response = await axios.get(
            `${API}/admin/download-waitlist-secure?key=launch-day-secret-123&t=${timestamp}`,
            { responseType: 'blob' }
        );
        // Create a fake link and click it to trigger the download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `waitlist_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        toast.success("Waitlist downloaded!");
    } catch (error) {
        console.error(error);
        toast.error("Failed to download. (Is the file empty?)");
    }
};

// NEW: Ghostbuster Button Component
const GhostbusterButton = () => {
    const [cleaning, setCleaning] = useState(false);

    const handleCleanup = async () => {
        if (!window.confirm("Run Ghostbuster? This will scan for and hide any 'orphan' sessions in the trending feed.")) return;

        setCleaning(true);
        try {
            const response = await axios.post(`${API}/admin/cleanup-ghosts`);
            const count = response.data.count;

            if (count > 0) {
                toast.success(`Ghostbustin' Complete! 👻`, {
                    description: `Removed ${count} broken clips from the feed.`
                });
            } else {
                toast.success('All Clear! ✨', {
                    description: 'No broken clips found in the feed.'
                });
            }
        } catch (error) {
            console.error(error);
            toast.error('Cleanup Failed', {
                description: 'Check server logs for details.'
            });
        } finally {
            setCleaning(false);
        }
    };

    return (
        <Button
            variant="outline"
            size="sm"
            onClick={handleCleanup}
            disabled={cleaning}
            className="text-purple-500 hover:text-purple-600 hover:bg-purple-50 border-purple-200"
            title="Scan for broken links in Trending Feed"
        >
            {cleaning ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
                <Ghost className="w-4 h-4 mr-2" />
            )}
            {cleaning ? 'Scanning...' : 'Fix Feed Ghosts'}
        </Button>
    );
};

const ClipCard = ({ clip, onDelete, onUpdate }) => {
    // Helper to toggle active/inactive status
    const toggleStatus = async () => {
        try {
            const newStatus = clip.status === 'active' ? 'inactive' : 'active';
            await axios.patch(`${API}/admin/clips/${clip.id}`, { status: newStatus });
            toast.success(`Clip marked as ${newStatus}`);
            onUpdate(); // Refresh the list
        } catch (error) {
            toast.error("Failed to update status");
        }
    };

    // Helper to toggle visibility (Featured vs Vault)
    const toggleVisibility = async () => {
        try {
            const newTier = clip.visibility_tier === 'featured' ? 'vault' : 'featured';
            await axios.patch(`${API}/admin/clips/${clip.id}`, { visibility_tier: newTier });
            toast.success(`Clip moved to ${newTier}`);
            onUpdate(); // Refresh the list
        } catch (error) {
            toast.error("Failed to update visibility");
        }
    };

    return (
        <div className={`card-base overflow-hidden transition-all duration-300 ${clip.status === 'inactive' ? 'opacity-60 grayscale' : ''}`}>
            <div className="aspect-video bg-muted relative group">
                {clip.thumbnail_url ? (
                    <img
                        src={clip.thumbnail_url}
                        alt={clip.title}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <Play className="w-10 h-10 text-muted-foreground" />
                    </div>
                )}

                {/* Status Badges */}
                <div className="absolute top-2 left-2 flex gap-1 z-10">
                    <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold shadow-sm ${clip.visibility_tier === 'featured' ? 'bg-primary text-black' : 'bg-purple-600 text-white'}`}>
                        {clip.visibility_tier === 'featured' ? 'Featured' : 'Vault'}
                    </span>
                    {clip.ai_title && (
                        <span className="px-2 py-0.5 rounded text-[10px] bg-blue-500 text-white shadow-sm" title={clip.ai_title}>
                            AI Tagged
                        </span>
                    )}
                </div>

                {/* Hover Actions Overlay */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                    <Button
                        size="icon"
                        variant="secondary"
                        onClick={(e) => { e.stopPropagation(); toggleVisibility(); }}
                        title={clip.visibility_tier === 'featured' ? "Move to Vault" : "Make Featured"}
                    >
                        {clip.visibility_tier === 'featured' ? <Lock className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
                    </Button>
                    <Button
                        size="icon"
                        variant={clip.status === 'active' ? "destructive" : "default"}
                        onClick={(e) => { e.stopPropagation(); toggleStatus(); }}
                        title={clip.status === 'active' ? "Deactivate" : "Activate"}
                    >
                        {clip.status === 'active' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                </div>
            </div>

            <div className="p-3">
                <h3 className="font-heading text-sm truncate mb-1" title={clip.title}>{clip.title}</h3>

                {clip.ai_title ? (
                    <p className="text-xs text-muted-foreground truncate mb-2">AI: {clip.ai_title}</p>
                ) : (
                    <p className="text-xs text-muted-foreground italic mb-2">Processing AI tags...</p>
                )}

                {/* Tag Pills */}
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
                    <div className="flex gap-1 overflow-hidden">
                        {clip.tags?.slice(0, 2).map(tag => (
                            <span key={tag} className="text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground whitespace-nowrap">
                                {tag}
                            </span>
                        ))}
                    </div>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-destructive hover:bg-destructive/20"
                        onClick={() => onDelete(clip)}
                    >
                        <Trash2 className="w-3 h-3" />
                    </Button>
                </div>
            </div>
        </div>
    );
};

const ReportLogCard = ({ report }) => {
    const isHidden = report.ai_verdict === true;
    return (
        <div className="card-base p-4 text-sm">
            <div className="flex flex-col md:flex-row gap-4 justify-between items-start">
                <div className="space-y-1 flex-1">
                    <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${isHidden ? 'bg-destructive/20 text-destructive' : 'bg-green-500/20 text-green-500'}`}>
                            {isHidden ? 'AI HID THIS' : 'AI MARKED SAFE'}
                        </span>
                        <span className="text-muted-foreground text-xs">{new Date(report.created_at).toLocaleString()}</span>
                    </div>
                    <div className="font-heading text-base">Reported for: <span className="text-primary">{report.reason}</span></div>
                    <p className="text-muted-foreground">Video: <a href={`/watch/${report.share_session_id}`} target="_blank" rel="noreferrer" className="hover:text-primary underline">{report.session_title}</a></p>
                    <div className="bg-muted/50 p-3 rounded mt-2 border border-border/50">
                        <p className="text-xs font-mono text-muted-foreground mb-1 uppercase">AI Reasoning:</p>
                        <p className="italic text-muted-foreground">"{report.ai_reasoning || 'No reasoning provided'}"</p>
                    </div>
                </div>
                <div className="text-right text-xs text-muted-foreground font-mono shrink-0">Reporter IP: {report.reporter_ip}</div>
            </div>
        </div>
    );
};

const ModerationCard = ({ session, onRestore, onBan }) => {
    return (
        <div className="card-base p-4">
            <div className="flex flex-col md:flex-row items-start gap-4">
                <a href={`/watch/${session.id}`} target="_blank" rel="noreferrer" className="w-full md:w-48 aspect-video bg-black rounded relative group block shrink-0 overflow-hidden">
                    {session.clip?.thumbnail_url ? (
                        <img src={session.clip.thumbnail_url} alt={session.title} className="w-full h-full object-cover opacity-60 group-hover:opacity-40 transition-opacity" />
                    ) : <div className="w-full h-full flex items-center justify-center bg-muted"><Film className="w-8 h-8 text-muted-foreground" /></div>}
                    <div className="absolute inset-0 flex items-center justify-center"><Play className="w-10 h-10 text-white opacity-80 group-hover:opacity-100 transition-all scale-95 group-hover:scale-110" /></div>
                </a>
                <div className="flex-1 min-w-0 space-y-2">
                    <div><h4 className="font-heading text-sm uppercase truncate">{session.title}</h4><p className="text-xs text-muted-foreground">ID: {session.id} • Creator: {session.creator_user_id}</p></div>
                    <div className="bg-muted/50 p-2 rounded text-xs font-mono text-muted-foreground line-clamp-2">"{session.subtitle_data?.map(s => s.text).join(' ')}"</div>
                </div>
                <div className="flex flex-row md:flex-col gap-2 w-full md:w-auto shrink-0 mt-2 md:mt-0">
                    <Button size="sm" className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white w-full md:w-auto" onClick={() => onRestore(session)}><Eye className="w-3 h-3" />Restore</Button>
                    <Button size="sm" variant="destructive" className="gap-2 w-full md:w-auto" onClick={() => onBan(session)}><EyeOff className="w-3 h-3" />Ban & Delete</Button>
                </div>
            </div>
        </div>
    );
};

// --- MAIN COMPONENT ---

export default function Admin() {
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const fileInputRef = useRef(null);

    // Data State
    const [clips, setClips] = useState([]);
    const [loadingClips, setLoadingClips] = useState(true);
    const [moderationQueue, setModerationQueue] = useState([]);
    const [reportLog, setReportLog] = useState([]);
    const [viewMode, setViewMode] = useState('queue');
    const [loadingModeration, setLoadingModeration] = useState(true);
    const [stats, setStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(true);

    // Bulk Upload State
    const [uploadQueue, setUploadQueue] = useState([]);
    const [isProcessingQueue, setIsProcessingQueue] = useState(false);

    // Delete Dialog
    const [deleteDialog, setDeleteDialog] = useState({ open: false, clip: null });

    // --- EFFECT: Auth Check & Initial Fetch ---
    useEffect(() => {
        if (!isAdmin) {
            navigate('/');
            return;
        }
        fetchClips();
        fetchModerationQueue();
        fetchReportLog();
        fetchStats();
    }, [isAdmin, navigate]);

    // --- FETCH ACTIONS ---
    const fetchClips = async () => {
        try {
            // Add timestamp to prevent caching
            const response = await axios.get(`${API}/clips?t=${Date.now()}`);
            setClips(response.data);
        } catch (error) {
            console.error('Failed to fetch clips:', error);
        } finally {
            setLoadingClips(false);
        }
    };

    const fetchModerationQueue = async () => {
        try {
            const response = await axios.get(`${API}/admin/moderation-queue`);
            setModerationQueue(response.data);
        } catch (error) { console.error(error); } finally { setLoadingModeration(false); }
    };

    const fetchReportLog = async () => {
        try {
            const token = localStorage.getItem('subhack_token');
            const { data } = await axios.get(`${API}/admin/reports-log`, { headers: { Authorization: `Bearer ${token}` } });
            setReportLog(data);
        } catch (error) { console.error(error); }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem('subhack_token');
            const { data } = await axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } });
            setStats(data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingStats(false);
        }
    };

    const handleDownloadUsers = async (status) => {
        const toastId = toast.loading(`Generating ${status} users CSV...`);
        try {
            const token = localStorage.getItem('subhack_token');
            const response = await axios.get(`${API}/admin/users/export/${status}`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob', // Critical: tells Axios to expect a file, not JSON
            });
            
            // Create a temporary hidden link to trigger the browser download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `subhack_${status}_users.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            toast.success(`Successfully downloaded ${status} users!`, { id: toastId });
        } catch (error) {
            console.error(`Failed to download ${status} users:`, error);
            toast.error(`Failed to download CSV`, { id: toastId });
        }
    };

    // --- BULK UPLOAD LOGIC ---

    const handleBulkSelect = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        // Transform File objects into queue items
        const newQueue = files.map(file => ({
            id: Math.random().toString(36).substr(2, 9),
            file,
            title: file.name.replace(/\.[^/.]+$/, ""), // Remove extension
            status: 'pending', // pending, uploading, done, error
            progress: 0
        }));

        // REPLACEMENT LOGIC: Overwrite the queue instead of appending
        setUploadQueue(newQueue);

        // Reset the input value so the same files can be selected again if needed
        e.target.value = null;
    };

    const processQueue = async () => {
        setIsProcessingQueue(true);
        const pendingItems = uploadQueue.filter(i => i.status === 'pending');

        // Process sequentially
        for (const item of pendingItems) {
            // Update UI to "Uploading"
            setUploadQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'uploading' } : q));

            try {
                // 1. Get Duration (Client side)
                const duration = await new Promise((resolve) => {
                    const video = document.createElement('video');
                    video.preload = 'metadata';
                    video.onloadedmetadata = () => resolve(video.duration);
                    video.src = URL.createObjectURL(item.file);
                });

                // 2. Get Presigned URL
                const presigned = await axios.post(`${API}/admin/clips/presigned-url`, {
                    filename: item.file.name,
                    content_type: item.file.type,
                });

                // 3. Upload to R2
                await axios.put(presigned.data.upload_url, item.file, {
                    headers: {
                        'Content-Type': item.file.type,
                        'Authorization': undefined
                    },
                    onUploadProgress: (ev) => {
                        const pct = Math.round((ev.loaded * 100) / ev.total);
                        setUploadQueue(prev => prev.map(q => q.id === item.id ? { ...q, progress: pct } : q));
                    }
                });

                // 4. Create DB Record (Triggers AI analysis in background)
                await axios.post(`${API}/admin/clips?file_key=${encodeURIComponent(presigned.data.file_key)}`, {
                    title: item.title,
                    duration: duration
                });

                // 5. Mark Done
                setUploadQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'done', progress: 100 } : q));

            } catch (error) {
                console.error("Upload failed for", item.title, error);
                setUploadQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: 'error' } : q));
            }
        }

        setIsProcessingQueue(false);
        fetchClips(); // Refresh the main library
        toast.success("Batch processing complete!");
    };

    const removeQueueItem = (id) => {
        setUploadQueue(uploadQueue.filter(item => item.id !== id));
    };

    // --- DELETE LOGIC (Hard Delete) ---
    const handleDeleteClip = async () => {
        if (!deleteDialog.clip) return;
        try {
            await axios.delete(`${API}/admin/clips/${deleteDialog.clip.id}`);
            setClips(clips.filter(c => c.id !== deleteDialog.clip.id));
            toast.success('Clip deleted permanently');
        } catch (error) {
            toast.error('Failed to delete clip');
        } finally {
            setDeleteDialog({ open: false, clip: null });
        }
    };

    // --- MODERATION ACTIONS ---
    const handleRestoreSession = async (session) => {
        try {
            await axios.post(`${API}/admin/restore/${session.id}`);
            setModerationQueue(moderationQueue.filter(s => s.id !== session.id));
            toast.success('Session restored');
        } catch (error) { toast.error('Failed'); }
    };

    const handleBanSession = async (session) => {
        if (!window.confirm(`Permanently ban "${session.title}"?`)) return;
        try {
            const token = localStorage.getItem('subhack_token');
            await axios.delete(`${API}/session/${session.id}`, { headers: { Authorization: `Bearer ${token}` } });
            setModerationQueue(moderationQueue.filter(s => s.id !== session.id));
            toast.success('Banned');
        } catch (error) { toast.error('Failed'); }
    };

    if (!isAdmin) return null;

    return (
        <Layout>
            <div className="min-h-screen py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Header */}
                    <div className="mb-12">
                        <h1 className="font-brand text-4xl md:text-5xl uppercase mb-4">
                            Admin <span className="text-primary">Dashboard</span>
                        </h1>
                        <p className="text-muted-foreground">Manage clips, automated tagging, and moderation.</p>
                    </div>

                    {/* --- NEW STATS WIDGET --- */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        {/* Free Users Card - NOW CLICKABLE */}
                        <div 
                            className="card-base p-4 border border-border bg-card/50 cursor-pointer hover:bg-primary/10 hover:border-primary/30 transition-all duration-200 group"
                            onClick={() => handleDownloadUsers('free')}
                            title="Click to download CSV of Free Users"
                        >
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <Users className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                                    <h3 className="font-heading text-sm uppercase text-muted-foreground group-hover:text-primary transition-colors">Free Users</h3>
                                </div>
                            </div>
                            {loadingStats ? <Skeleton className="h-8 w-16" /> : <p className="text-2xl font-bold">{stats?.free_users || 0}</p>}
                        </div>
                        {/* Pro Users Card - NOW CLICKABLE */}
                        <div 
                            className="card-base p-4 border border-border bg-card/50 cursor-pointer hover:bg-primary/10 hover:border-primary/30 transition-all duration-200 group"
                            onClick={() => handleDownloadUsers('pro')}
                            title="Click to download CSV of Pro Users"
                        >
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <Crown className="w-4 h-4 text-primary" />
                                    <h3 className="font-heading text-sm uppercase text-primary">Pro Users</h3>
                                </div>
                            </div>
                            {loadingStats ? <Skeleton className="h-8 w-16" /> : <p className="text-2xl font-bold">{stats?.pro_users || 0}</p>}
                        </div>
                        <div className="card-base p-4 border border-border bg-card/50">
                            <div className="flex items-center gap-2 mb-2">
                                <RefreshCw className="w-4 h-4 text-emerald-500" />
                                <h3 className="font-heading text-sm uppercase text-emerald-500">Conversion Rate</h3>
                            </div>
                            {loadingStats ? <Skeleton className="h-8 w-16" /> : <p className="text-2xl font-bold">{stats?.conversion_rate || "0%"}</p>}
                        </div>
                        <div className="card-base p-4 border border-border bg-card/50">
                            <div className="flex items-center gap-2 mb-2">
                                <Film className="w-4 h-4 text-purple-500" />
                                <h3 className="font-heading text-sm uppercase text-purple-500">Total Clips</h3>
                            </div>
                            {loadingStats ? <Skeleton className="h-8 w-16" /> : <p className="text-2xl font-bold">{stats?.total_clips || 0}</p>}
                        </div>
                    </div>

                    <Tabs defaultValue="clips" className="space-y-8">
                        <TabsList>
                            <TabsTrigger value="clips" className="gap-2"><Film className="w-4 h-4" /> Library</TabsTrigger>
                            <TabsTrigger value="moderation" className="gap-2">
                                <AlertCircle className="w-4 h-4" /> Moderation
                                {moderationQueue.length > 0 && <span className="ml-1 bg-destructive text-destructive-foreground text-xs px-2 py-0.5 rounded-full">{moderationQueue.length}</span>}
                            </TabsTrigger>
                        </TabsList>

                        {/* --- CLIPS TAB --- */}
                        <TabsContent value="clips" className="space-y-8">

                            {/* BULK UPLOADER CARD */}
                            <div className="card-base p-6 border-dashed border-2 border-border bg-card/50">
                                <div className="flex flex-col md:flex-row gap-8">
                                    {/* Drop Zone */}
                                    <div
                                        className={`flex-1 flex flex-col items-center justify-center p-8 border-2 border-dashed border-muted-foreground/20 rounded-lg transition-colors ${isProcessingQueue ? 'opacity-50 cursor-not-allowed' : 'hover:bg-muted/50 cursor-pointer'}`}
                                        onClick={() => {
                                            if (!isProcessingQueue) fileInputRef.current?.click();
                                        }}
                                    >
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            multiple // Enable Bulk Selection
                                            accept="video/mp4,video/webm,video/quicktime"
                                            className="hidden"
                                            onChange={handleBulkSelect}
                                            disabled={isProcessingQueue}
                                        />
                                        <Upload className="w-12 h-12 text-primary mb-4" />
                                        <h3 className="font-heading text-lg uppercase mb-1">Bulk Upload</h3>
                                        <p className="text-sm text-muted-foreground text-center">
                                            Select multiple video files.<br />AI will auto-tag them after upload.
                                        </p>
                                    </div>

                                    {/* Queue List */}
                                    <div className="flex-1 space-y-4">
                                        <div className="flex items-center justify-between">
                                            <h3 className="font-heading text-sm uppercase">Upload Queue ({uploadQueue.length})</h3>
                                            {uploadQueue.some(i => i.status === 'pending') && (
                                                <Button size="sm" onClick={processQueue} disabled={isProcessingQueue} className="btn-primary">
                                                    {isProcessingQueue ? <><Loader2 className="w-3 h-3 animate-spin mr-2" />Processing</> : 'Start Upload'}
                                                </Button>
                                            )}
                                        </div>

                                        <div className="h-64 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                                            {uploadQueue.length === 0 && (
                                                <p className="text-sm text-muted-foreground text-center py-8">No files selected.</p>
                                            )}
                                            {uploadQueue.map(item => (
                                                <div key={item.id} className="flex items-center gap-3 p-3 bg-background rounded border border-border">
                                                    <Film className="w-4 h-4 text-muted-foreground shrink-0" />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium truncate">{item.title}</p>
                                                        {item.status === 'uploading' && (
                                                            <div className="w-full h-1 bg-muted rounded-full mt-1 overflow-hidden">
                                                                <div className="h-full bg-primary transition-all duration-300" style={{ width: `${item.progress}%` }} />
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Status Icons */}
                                                    {item.status === 'pending' && <span className="text-xs text-muted-foreground">Ready</span>}
                                                    {item.status === 'done' && <CheckCircle className="w-4 h-4 text-green-500" />}
                                                    {item.status === 'error' && <AlertCircle className="w-4 h-4 text-destructive" />}
                                                    {item.status === 'pending' && (
                                                        <button onClick={() => removeQueueItem(item.id)} className="text-muted-foreground hover:text-foreground">
                                                            <Trash2 className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* LIBRARY GRID */}
                            <div>
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="font-heading text-lg uppercase">
                                        Library ({clips.length})
                                    </h2>
                                    <div className="flex gap-2">
                                        {/* ADDED GHOSTBUSTER HERE */}
                                        <GhostbusterButton />

                                        <Button variant="outline" size="sm" onClick={fetchClips}>
                                            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                                        </Button>
                                    </div>
                                </div>

                                {loadingClips ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                        {[...Array(4)].map((_, i) => (
                                            <div key={i} className="card-base overflow-hidden">
                                                <Skeleton className="aspect-video" />
                                                <div className="p-4 space-y-2"><Skeleton className="h-4 w-3/4" /><Skeleton className="h-3 w-1/2" /></div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                        {clips.map((clip) => (
                                            <ClipCard
                                                key={clip.id}
                                                clip={clip}
                                                onDelete={(c) => setDeleteDialog({ open: true, clip: c })}
                                                onUpdate={fetchClips}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </TabsContent>

                        {/* --- MODERATION TAB --- */}
                        <TabsContent value="moderation" className="space-y-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="font-heading text-lg uppercase">
                                    {viewMode === 'queue' ? 'Action Required' : 'Full Report Log'}
                                </h2>
                                <div className="flex bg-muted p-1 rounded-lg">
                                    <button onClick={() => setViewMode('queue')} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'queue' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`}>Queue</button>
                                    <button onClick={() => setViewMode('log')} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'log' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground'}`}>Log</button>
                                </div>
                            </div>

                            {viewMode === 'queue' ? (
                                loadingModeration ? <Skeleton className="h-24" /> : moderationQueue.length > 0 ? (
                                    <div className="space-y-4">
                                        {moderationQueue.map((session) => (
                                            <ModerationCard key={session.id} session={session} onRestore={handleRestoreSession} onBan={handleBanSession} />
                                        ))}
                                    </div>
                                ) : <div className="text-center py-12"><CheckCircle className="w-12 h-12 text-primary mx-auto mb-4" /><p className="text-muted-foreground">All clear.</p></div>
                            ) : (
                                <div className="space-y-4">
                                    {reportLog.map((report) => <ReportLogCard key={report.id} report={report} />)}
                                </div>
                            )}
                        </TabsContent>
                    </Tabs>
                </div>
            </div>

            {/* DELETE DIALOG */}
            <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, clip: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading uppercase">Delete Clip?</DialogTitle>
                        <DialogDescription>
                            This will permanently delete "{deleteDialog.clip?.title}" and all associated sessions.
                            To hide it without deleting data, use the "Deactivate" (Eye) button on the card instead.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialog({ open: false, clip: null })}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDeleteClip}>Delete Permanently</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}